import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiOutlined, CheckCircleOutlined, DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, Form, Input, message, Modal, Popconfirm, Row, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { api } from "../services/apiClient";
import { canUseUserFilter, fetchUserOptions, userQuery } from "../utils/adminUserFilter";

const { Title, Text } = Typography;

const statusLabel = (status) => ({ ACTIVE: "Hoạt động", ERROR: "Lỗi", DISCONNECTED: "Mất kết nối" }[status] || "Mất kết nối");
const statusColor = (status) => ({ ACTIVE: "green", ERROR: "red", DISCONNECTED: "orange" }[status] || "orange");

export default function AutoFacebookPages() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [connectingPage, setConnectingPage] = useState(null);
  const [userAccessToken, setUserAccessToken] = useState("");
  const [form] = Form.useForm();
  const showUserFilter = canUseUserFilter();

  const load = useCallback(async () => {
    try { setItems((await api.get(`/api/facebook-pages${userQuery(selectedUserId)}`)) || []); }
    catch (error) { message.error(error.message); }
  }, [selectedUserId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showUserFilter) fetchUserOptions().then(setUsers).catch((error) => message.error(error.message)); }, [showUserFilter]);

  const data = useMemo(() => {
    const keyword = search.toLowerCase();
    return items.filter((item) => `${item.pageName || ""} ${item.pageId || ""} ${item.pageUrl || ""}`.toLowerCase().includes(keyword));
  }, [items, search]);

  const openModal = (item = null) => {
    setEditing(item);
    form.setFieldsValue({ graphApiVersion: "v22.0", ...(item || {}), appSecret: "", pageAccessToken: "" });
    setOpen(true);
  };

  const closeModal = () => { setOpen(false); setEditing(null); form.resetFields(); };

  const save = async () => {
    try {
      const values = await form.validateFields();
      await api.post("/api/facebook-pages", { ...editing, ...values });
      message.success("Đã lưu Page Facebook");
      closeModal();
      load();
    } catch (error) { if (error.message) message.error(error.message); }
  };

  const connect = async (item) => {
    try {
      const url = await api.get(`/api/facebook-pages/${item.id}/connect`);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) { message.error(error.message); }
  };

  const connectWithToken = async () => {
    if (!userAccessToken.trim() || !connectingPage) {
      message.warning("Nhập User Access Token từ Meta Graph API Explorer");
      return;
    }
    try {
      await api.post(`/api/facebook-pages/${connectingPage.id}/connect-token`, { userAccessToken: userAccessToken.trim() });
      message.success("Đã lấy Page access token và kết nối Facebook Page");
      setConnectingPage(null);
      setUserAccessToken("");
      load();
    } catch (error) { message.error(error.message); }
  };

  const verify = async (item) => {
    try {
      await api.post(`/api/facebook-pages/${item.id}/verify`);
      message.success("Facebook Page token hoạt động bình thường");
    } catch (error) { message.error(error.message); }
    finally { load(); }
  };

  const remove = async (id) => {
    try { await api.delete(`/api/facebook-pages/${id}`); message.success("Đã xóa Page Facebook"); load(); }
    catch (error) { message.error(error.message); }
  };

  const columns = [
    { title: "PAGE FACEBOOK", render: (_, item) => <div><Text strong>{item.pageName}</Text><br /><Text type="secondary">{item.pageId || item.pageUrl || "-"}</Text></div> },
    { title: "TRẠNG THÁI", width: 130, render: (_, item) => <Tag color={statusColor(item.status)}>{statusLabel(item.status)}</Tag> },
    { title: "GHI CHÚ", dataIndex: "notes", render: (value) => value || "-" },
    {
      title: "THAO TÁC", width: 180,
      render: (_, item) => (
        <Space className="provider-actions">
          <Tooltip title="Kết nối Facebook bằng OAuth"><Button aria-label="Kết nối Facebook bằng OAuth" className="provider-action-button" icon={<LinkOutlined />} onClick={() => connect(item)} /></Tooltip>
          <Tooltip title="Kết nối bằng User Access Token"><Button aria-label="Kết nối bằng User Access Token" className="provider-action-button" icon={<ApiOutlined />} onClick={() => setConnectingPage(item)} /></Tooltip>
          <Tooltip title="Kiểm tra token"><Button aria-label="Kiểm tra token" className="provider-action-button" icon={<CheckCircleOutlined />} onClick={() => verify(item)} /></Tooltip>
          <Tooltip title="Sửa Page"><Button aria-label="Sửa Page" className="provider-action-button" icon={<EditOutlined />} onClick={() => openModal(item)} /></Tooltip>
          <Popconfirm title="Xóa Page này?" onConfirm={() => remove(item.id)}><Tooltip title="Xóa Page"><Button aria-label="Xóa Page" danger className="provider-action-button" icon={<DeleteOutlined />} /></Tooltip></Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="page-heading"><div><Title level={3}>Page Facebook</Title><Text type="secondary">Kết nối và quản lý các fanpage dùng để Auto Upload.</Text></div></div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm Page Facebook" value={search} onChange={(event) => setSearch(event.target.value)} />
          {showUserFilter && <Select allowClear showSearch optionFilterProp="label" placeholder="Tìm theo người dùng" style={{ minWidth: 240 }} value={selectedUserId} onChange={setSelectedUserId} options={users} />}
          <Button className="auto-toolbar-primary" type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Thêm Page</Button>
        </div>
        <Table rowKey="id" columns={columns} dataSource={data} />
      </Card>
      <Modal width={860} title={editing ? "Sửa Page Facebook" : "Thêm Page Facebook"} visible={open} onCancel={closeModal} onOk={save} okText="Lưu">
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="pageName" label="Tên Page" rules={[{ required: true, message: "Nhập tên Page" }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="pageId" label="Page ID" rules={[{ required: true, message: "Nhập Page ID để kết nối" }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="appId" label="Facebook App ID" rules={[{ required: true, message: "Nhập Facebook App ID" }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="graphApiVersion" label="Graph API version"><Input placeholder="v22.0" /></Form.Item></Col>
            <Col span={12}><Form.Item name="appSecret" label="Facebook App Secret"><Input.TextArea rows={3} placeholder={editing ? "Để trống nếu không đổi App Secret" : "Nhập App Secret"} /></Form.Item></Col>
            <Col span={12}><Form.Item name="pageAccessToken" label="Page access token"><Input.TextArea rows={3} placeholder={editing ? "Để trống nếu dùng token đã lưu hoặc bấm Kết nối Facebook" : "Có thể để trống rồi bấm Kết nối Facebook"} /></Form.Item></Col>
            <Col span={12}><Form.Item name="pageUrl" label="Page URL"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="notes" label="Ghi chú"><Input.TextArea rows={3} /></Form.Item></Col>
          </Row>
          <Text type="secondary"><ApiOutlined /> Lưu Page trước, rồi bấm icon liên kết trong danh sách để đăng nhập Facebook và cấp quyền.</Text>
        </Form>
      </Modal>
      <Modal title={`Kết nối ${connectingPage?.pageName || "Facebook Page"}`} visible={Boolean(connectingPage)}
        onCancel={() => { setConnectingPage(null); setUserAccessToken(""); }} onOk={connectWithToken} okText="Kết nối">
        <Text type="secondary">Dán User Access Token lấy từ Meta Graph API Explorer. Token chỉ dùng cho lần kết nối này; hệ thống đổi sang long-lived token để lấy Page access token và không lưu User Access Token.</Text>
        <Input.Password value={userAccessToken} onChange={(event) => setUserAccessToken(event.target.value)} placeholder="User Access Token" style={{ marginTop: 16 }} />
      </Modal>
    </>
  );
}
